import { SIGN_IN_POOL } from '@bcecloud/portal-ui';
import { useClerkPool } from '@/components/MyEventsAuthProvider';
import { getViewAsCustomer } from '@/lib/view-as';
import { ViewAsBanner } from '@/components/ViewAsBanner';
import { MyEventsShell } from '@/components/MyEventsShell';
import { CustomerPickerPage } from '@/pages/staff/CustomerPickerPage';

/**
 * Decides what a My Events session actually shows.
 *
 * A customer sees their own events, as always. A BCE staff session has no
 * events of its own — the whole point of it being here is to look at somebody
 * else's — so without a chosen customer it gets the picker: every event a
 * customer can open, by the people who can open it. Once a customer is chosen
 * (here, or through a view-as link from BCEcloud) the portal carries a banner
 * naming them.
 *
 * The no-customer branch must never fall through to the routed page: a staff
 * session with no view-as target is unscoped, and the events list would show
 * every event in the system inside a customer-shaped portal.
 *
 * Reads the pool from the auth provider, which resolves it synchronously at
 * mount — so a staff member never sees a flash of an empty customer portal,
 * and the answer cannot drift from the Clerk instance actually mounted.
 *
 * @param props - The routed page.
 * @returns The portal, or the staff signpost.
 */
export function StaffViewGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { pool } = useClerkPool();
  const isStaff = pool === SIGN_IN_POOL.staff;

  if (!isStaff) {
    return <MyEventsShell>{children}</MyEventsShell>;
  }

  const customer = getViewAsCustomer();
  if (!customer) {
    return (
      <MyEventsShell>
        <CustomerPickerPage />
      </MyEventsShell>
    );
  }

  return <MyEventsShell banner={<ViewAsBanner customer={customer} />}>{children}</MyEventsShell>;
}
